import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isUserAuthorized } from '../../util/userHelpers';
import { pathByRouteName } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { Button, TwilioVerify } from '../../components';
import { verifyStart, verifyCheck } from '../../util/onboarding';
import { clearCfDestination, inspectCfDestination } from '../../util/cfOrder';
import { noteClaimOutcome } from '../../util/cfClaimSignals';
import {
  isInOnboardingCohort,
  onboardingAudience,
  isOnboardingComplete,
  hasCfPurchase,
  getKnownPhone,
} from '../../util/onboardingCohort';
import { checkSellerListing, fetchOnboardingState, acceptTerms } from './OnboardingModal.duck';

import css from './OnboardingModal.module.css';

const TERMS_URL = '/terms-of-service';
const PRIVACY_URL = '/privacy-policy';

// Finalizing-window poll (incomplete + missing:[]): fast poll up to the cap, then
// a slow poll that NEVER stops + a manual refresh affordance — so a slow
// server-side completion never strands the user on a permanent spinner (N1/T3).
const FINALIZING_POLL_MS = 2500;
const FINALIZING_POLL_CAP = 12;
const FINALIZING_SLOW_POLL_MS = 15000;

// Authoritative-GET error backoff (F1/T1): an in-cohort user whose onboarding-state
// GET errors must KEEP the gate up (fail closed) and auto-retry on a bounded
// exponential backoff, with a manual retry as the floor once the cap is reached.
const ERROR_RETRY_CAP = 6;
const ERROR_RETRY_BASE_MS = 2000;
const ERROR_RETRY_MAX_MS = 15000;

const PAGE_ROOT_ID = 'root';
const PORTAL_ROOT_ID = 'portal-root';

// Success-CTA destinations (Maroon Crown design pass): CF sellers go create
// their listing, migrated sellers go to their existing listings.
//
// NOTE (list-your-horse routing): this is the post-onboarding success screen —
// a Button + history.push, NOT a "List your horse" link, so it does NOT use the
// <ListYourHorseLink> wrapper. It is already consistent with the shared
// `listYourHorseGoesToWizard` rule by construction: `audience` is 'cf' iff the
// user has a cfPurchaseId (and success is only ever shown to a logged-in,
// in-cohort user), so the cf branch → NewListingPage is exactly the wizard the
// wrapper would pick. Migrated sellers (no cfPurchaseId) are deliberately sent
// to their existing listings here, NOT to the CF landing page — the CF landing
// is a "go buy a slot" surface, wrong for someone who just finished onboarding.
//
// Story 3.24 — BOTH branches yield to a stored post-purchase destination when
// one exists (`cfOrder.js`, `tph_cf_destination`, written by the ClickFunnels
// prompt at render). `audience` is read off the redux currentUser, which was
// fetched at login — BEFORE tph-api attached the just-bought slot — so a brand
// new buyer reads as `migrated` here and this CTA sent them to their (empty)
// listings page, whose only door is the CF checkout: back at the payment page
// with a slot already paid for. The stored destination is `/l/new?cf_order=…`,
// which fires the attach and lands in the wizard.
const SUCCESS_ROUTE_BY_AUDIENCE = {
  cf: 'NewListingPage',
  migrated: 'ManageListingsPage',
};

const pendingFrom = missing => ({
  phone: Array.isArray(missing) && missing.includes('phone'),
  tos: Array.isArray(missing) && (missing.includes('tos') || missing.includes('tos_version')),
});

// Fire-and-forget dispatch helper. The thunk wrappers call `.unwrap()` (which
// rejects on the error path), but errors are already captured in redux state
// (`fetchError`) and surfaced via the error screen + retry — so a caller that
// isn't awaiting the result intentionally swallows the rejection to avoid an
// unhandled-promise warning.
const swallow = promise => {
  if (promise && typeof promise.catch === 'function') {
    promise.catch(() => {});
  }
  return promise;
};

/**
 * Story 9.13 — the app-wide, cohort-gated, GET-authoritative three-step
 * onboarding hard gate (decisions 1–5). Mounted once at the Topbar level beside
 * ModalMissingInformation. Visuals follow the "Maroon Crown" reference
 * (design-reference/onboarding-modal.html): a maroon crown (logotype + eyebrow +
 * headline) beside a cream body (step dots, copy, one gold CTA) — a 720px
 * horizontal "wide stage" card on desktop (crown as left rail), a full-screen
 * takeover on mobile (crown band on top, CTA pinned to the bottom thumb zone).
 *
 * Gate engagement is COHORT-based (LI-8): a committed-to-selling user (≥1 listing
 * in {active,on-hold,cancelled,sold} OR cfPurchaseId) whose onboarding is
 * incomplete. The active screen is derived from the GET `missing` array (LI-11):
 * the welcome screen greets EVERY first entry (any step pending) until
 * acknowledged this session, with audience + pending-step copy variants; after
 * that, partial resumes go straight to the lone pending step and never re-run a
 * satisfied one. Teardown happens ONLY on `{status:'complete'}` (LI-1); a
 * finalizing window (`incomplete` + `missing:[]`) keeps the gate up and re-polls
 * (LI-2). The gate is un-dismissable while incomplete (LI-3). When completion
 * lands AFTER the user actively onboarded this session (saw at least one step
 * screen), a dismissable success screen shows before teardown; users already
 * complete on page load (the short-circuit path) never see it.
 *
 * FAIL CLOSED (code-review F1/N1/N2/F2): an in-cohort, incomplete user is never
 * left un-gated by a loading state, a transient GET error, or a slow finalize.
 * While the authoritative GET is in flight the gate shows a loading screen (T2);
 * on a GET error it stays up with a retry affordance and auto-retries (T1); the
 * finalizing poll never gives up (T3); and the gate engages whenever the stock
 * email-verify gate will NOT fire, so exactly one hard gate is shown — never zero
 * (T4/F2).
 *
 * This is intentionally a client-only gate (no SSR loadData): like the stock
 * ModalMissingInformation it evaluates against currentUser + a session-bound API
 * call, so it orchestrates from effects and renders nothing on the server.
 *
 * @component
 * @param {Object} [props]
 * @param {number} [props.finalizingPollCap] fast-poll count before the slow-poll
 *   + refresh affordance (test seam; defaults to FINALIZING_POLL_CAP).
 * @returns {JSX.Element|null}
 */
const OnboardingModal = ({ finalizingPollCap = FINALIZING_POLL_CAP } = {}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const rawCurrentUser = useSelector(state => state.user.currentUser);
  const {
    status,
    missing,
    isComplete,
    hasSellerListing,
    sellerListingChecked,
    fetchInProgress,
    fetchError,
    operatorSession,
  } = useSelector(state => state.OnboardingModal);

  const currentUser = ensureCurrentUser(rawCurrentUser);
  const currentUserReady = !!(currentUser && currentUser.id && isUserAuthorized(currentUser));

  const cf = currentUserReady && hasCfPurchase(currentUser);
  const completedShortCircuit = currentUserReady && isOnboardingComplete(currentUser);
  const inCohort = currentUserReady && isInOnboardingCohort(currentUser, { hasSellerListing });

  // Story 9.13 F2 (revised) — email verification is NON-BLOCKING. The welcome
  // screen's hard gate is phone + terms ONLY; email confirmation rides along in
  // the background (the verification email still sends at account creation —
  // Sharetribe default, NOT disabled). So engagement does NOT depend on
  // emailVerified: the gate engages for any in-cohort, onboarding-incomplete user
  // regardless of email state, and never suppresses itself to defer to the email
  // reminder. The stock ModalMissingInformation email-verification reminder is
  // suppressed (see that component) so the two modals never stack.
  const eligible = isAuthenticated && currentUserReady && !completedShortCircuit && !isComplete;

  const [welcomeAcknowledged, setWelcomeAcknowledged] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [finalizingStalled, setFinalizingStalled] = useState(false);
  const [successDismissed, setSuccessDismissed] = useState(false);
  // Phase-2 swap for the phone screen: TwilioVerify reports when its code field
  // is shown (and the display-formatted number the code went to).
  const [codePhase, setCodePhase] = useState({ codeSent: false, displayNumber: '' });
  const pollCountRef = useRef(0);
  const retryCountRef = useRef(0);
  // Captured ONCE per session at first resolved entry with pending steps: which
  // steps were pending and how many (drives "Step {x} of {N}" + the dots) — and,
  // by being non-null, proves the user actively onboarded this session (the
  // success-screen precondition). Stays null on the completion short-circuit
  // path, so already-complete users can never see the success screen.
  const initialPendingRef = useRef(null);

  // Gate is visible whenever engaged for the cohort — across EVERY non-terminal
  // state: loading | error | step | finalizing. It closes only when onboarding is
  // complete (eligible flips false via isComplete). This is what makes loading
  // and error states fail CLOSED rather than leaking the app (T1/T2).
  //
  // Story 9.16 — `operatorSession` is the ONE exception, and it is not a client
  // opinion: it arrives only on the server-verified `operator` field of the
  // authoritative GET (httpOnly HMAC marker + uuid match, minted solely at the
  // Console-gated login-as callback). It touches `open` and nothing else — the
  // fetch effects below stay gated on eligible/inCohort/sellerListingChecked,
  // NOT on `open`, because the flag arrives ON the response those effects fetch.
  // Gating them on `open` would deadlock the gate permanently shut.
  const open = eligible && inCohort && !operatorSession;

  // Success screen (design pass): completion detected AFTER the user actively
  // onboarded this session holds the modal open with a dismissable success
  // screen. Purely derived — never armed on the already-complete-on-load path.
  const showSuccess = isComplete && initialPendingRef.current !== null && !successDismissed;

  const isFinalizing =
    open && status === 'incomplete' && Array.isArray(missing) && missing.length === 0;

  // Arm the auto-retry only when the authoritative GET has errored and no fetch
  // is currently in flight (so a pending retry doesn't reset the backoff).
  const shouldAutoRetry = open && status === null && !fetchInProgress && !!fetchError;

  useEffect(() => {
    setMounted(true);
  }, []);

  // ── Cohort resolution + authoritative GET orchestration ──────────────────
  useEffect(() => {
    if (!eligible) {
      return;
    }
    // CF users are in cohort without a listing query; everyone else needs the
    // seller-listing leg resolved first.
    if (!cf && !sellerListingChecked) {
      swallow(dispatch(checkSellerListing()));
      return;
    }
    if (!inCohort) {
      return;
    }
    if (status === null) {
      swallow(dispatch(fetchOnboardingState()));
    }
  }, [eligible, cf, inCohort, sellerListingChecked, status, dispatch]);

  // ── GET-error auto-retry (bounded exponential backoff) — fail closed ──────
  // Reset the backoff once a status resolves (success) so a later error starts
  // fresh; never reset mid-flight (that would defeat the backoff).
  useEffect(() => {
    if (status !== null) {
      retryCountRef.current = 0;
    }
  }, [status]);

  useEffect(() => {
    if (!shouldAutoRetry || retryCountRef.current >= ERROR_RETRY_CAP) {
      return undefined;
    }
    const delay = Math.min(ERROR_RETRY_BASE_MS * 2 ** retryCountRef.current, ERROR_RETRY_MAX_MS);
    const timer = setTimeout(() => {
      retryCountRef.current += 1;
      swallow(dispatch(fetchOnboardingState()));
    }, delay);
    return () => clearTimeout(timer);
  }, [shouldAutoRetry, fetchError, dispatch]);

  // ── Finalizing-window re-poll (incomplete + missing:[]) — never gives up ──
  useEffect(() => {
    if (!isFinalizing) {
      pollCountRef.current = 0;
      setFinalizingStalled(false);
      return undefined;
    }
    const past = pollCountRef.current >= finalizingPollCap;
    if (past && !finalizingStalled) {
      setFinalizingStalled(true);
    }
    const delay = past ? FINALIZING_SLOW_POLL_MS : FINALIZING_POLL_MS;
    const timer = setTimeout(() => {
      pollCountRef.current += 1;
      swallow(dispatch(fetchOnboardingState()));
    }, delay);
    return () => clearTimeout(timer);
  }, [isFinalizing, status, missing, dispatch, finalizingPollCap, finalizingStalled]);

  // ── Body scroll lock + page inert while the gate (or success) is shown ───
  const lockPage = open || showSuccess;
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const pageRoot = document.getElementById(PAGE_ROOT_ID);
    if (lockPage) {
      document.body.style.overflow = 'hidden';
      pageRoot?.setAttribute('inert', '');
    } else {
      document.body.style.overflow = '';
      pageRoot?.removeAttribute('inert');
    }
    return () => {
      document.body.style.overflow = '';
      pageRoot?.removeAttribute('inert');
    };
  }, [lockPage]);

  if ((!open && !showSuccess) || !mounted) {
    return null;
  }

  // First resolved entry with pending steps: lock in the session's step plan
  // (lazy ref init during render — set exactly once, before first paint of a
  // step screen, so the eyebrow/dots never flicker).
  if (
    initialPendingRef.current === null &&
    open &&
    status === 'incomplete' &&
    Array.isArray(missing) &&
    missing.length > 0
  ) {
    const p = pendingFrom(missing);
    initialPendingRef.current = {
      phone: p.phone,
      tos: p.tos,
      count: (p.phone ? 1 : 0) + (p.tos ? 1 : 0),
    };
  }

  const audience = onboardingAudience(currentUser);
  const { phone: phonePending, tos: tosPending } = pendingFrom(missing);
  const anyPending = phonePending || tosPending;

  let screen;
  if (showSuccess) {
    screen = 'success';
  } else if (status === null) {
    // GET not yet resolved: show a loading state (in flight, fail closed — T2) or
    // a retry affordance if it errored (fail closed, never fall through — T1).
    screen = fetchError ? 'error' : 'loading';
  } else if (!Array.isArray(missing) || missing.length === 0) {
    screen = 'finalizing';
  } else if (anyPending && !welcomeAcknowledged) {
    // The welcome screen greets EVERY first entry (≥1 step pending) until
    // acknowledged this session; copy varies by audience + pending steps.
    screen = 'welcome';
  } else if (phonePending) {
    screen = 'phone';
  } else {
    screen = 'tos';
  }

  const onPhoneVerified = () => {
    // Teardown is GET-driven: refresh onboarding-state so the next pending step
    // (or completion) is authoritative — never advance off a local flag alone.
    swallow(dispatch(fetchOnboardingState()));
  };

  const onAcceptTerms = () => {
    swallow(dispatch(acceptTerms()));
  };

  const onReloadState = () => {
    swallow(dispatch(fetchOnboardingState()));
  };

  const onSuccessCta = () => {
    setSuccessDismissed(true);
    // Story 3.24 — see SUCCESS_ROUTE_BY_AUDIENCE. This is the LAST consumer of
    // the stored destination on the signup and reset paths, so it clears it.
    // Pass C — includes the record this page's `/login` redirect handed off
    // (cfOrder.js `handOffCfDestination`): without it a buyer whose sign-in used
    // the stored value reached this button with nothing to route by.
    const destination = inspectCfDestination({ includeHandedOff: true });
    if (destination.status === 'live') {
      clearCfDestination();
      history.push(destination.path);
      return;
    }
    // No live destination. If this browser's claim record EXPIRED during this
    // claim, the buyer is about to land somewhere other than the claim — report
    // it (Sentry `claim_ended_elsewhere`, fail-open, no identifiers). The read
    // above cleared it from storage; the page's memory of it is what reports.
    const audienceTarget = pathByRouteName(SUCCESS_ROUTE_BY_AUDIENCE[audience], routeConfiguration);
    noteClaimOutcome({ surface: 'onboarding', target: audienceTarget });
    history.push(audienceTarget);
  };

  const onSuccessQuiet = () => {
    setSuccessDismissed(true);
  };

  const portalRoot = document.getElementById(PORTAL_ROOT_ID);
  if (!portalRoot) {
    return null;
  }

  // ── Maroon Crown shell pieces ─────────────────────────────────────────────
  const initialPending = initialPendingRef.current;
  const totalSteps = initialPending ? initialPending.count : 0;
  // Phone is always step 1 when it was in the entry plan; terms is step 2 after
  // a pending phone, else step 1.
  const tosStepIndex = initialPending && initialPending.phone ? 2 : 1;
  const currentStepIndex = screen === 'tos' ? tosStepIndex : 1;

  // Eyebrow per screen; a one-step plan reads "Almost done" instead of "Step 1 of 1".
  const stepEyebrow =
    totalSteps > 1 ? (
      <FormattedMessage
        id="OnboardingModal.eyebrowStep"
        values={{ current: currentStepIndex, total: totalSteps }}
      />
    ) : (
      <FormattedMessage id="OnboardingModal.eyebrowAlmostDone" />
    );
  const eyebrowByScreen = {
    welcome: <FormattedMessage id="OnboardingModal.eyebrowWelcome" />,
    phone: stepEyebrow,
    tos: stepEyebrow,
    finalizing: <FormattedMessage id="OnboardingModal.eyebrowFinalizing" />,
    success: <FormattedMessage id="OnboardingModal.eyebrowComplete" />,
  };
  const eyebrow = eyebrowByScreen[screen] || null;

  // Step dots (welcome/phone/tos, multi-step plans only).
  const showDots = totalSteps > 1 && ['welcome', 'phone', 'tos'].includes(screen);
  const dots = showDots ? (
    <div className={css.steps}>
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        const dotClass = classNames(css.dot, {
          [css.dotDone]: step < currentStepIndex,
          [css.dotCurrent]: step === currentStepIndex,
        });
        return <span key={step} className={dotClass} />;
      })}
    </div>
  ) : null;

  // Welcome copy variant: audience × pending steps.
  const welcomeVariant = phonePending && tosPending ? 'both' : phonePending ? 'phone' : 'tos';

  const titleByScreen = {
    loading: <FormattedMessage id="OnboardingModal.loadingTitle" />,
    error: <FormattedMessage id="OnboardingModal.errorTitle" />,
    welcome: <FormattedMessage id={`OnboardingModal.welcomeTitle.${audience}`} />,
    phone: codePhase.codeSent ? (
      <FormattedMessage id="OnboardingModal.phoneCodeTitle" />
    ) : (
      <FormattedMessage id="OnboardingModal.phoneTitle" />
    ),
    tos: <FormattedMessage id="OnboardingModal.tosTitle" />,
    finalizing: <FormattedMessage id="OnboardingModal.finalizingTitle" />,
    success: <FormattedMessage id={`OnboardingModal.successTitle.${audience}`} />,
  };

  // The welcome body is TWO paragraphs (readability pass): P1 introduces The
  // Premier Line, P2 frames the steps — split at the i18n level (.intro/.steps)
  // so every audience × pending variant renders the same two-block shape.
  const bodyByScreen = {
    loading: <FormattedMessage id="OnboardingModal.loadingBody" />,
    error: <FormattedMessage id="OnboardingModal.errorBody" />,
    welcome: (
      <>
        <p className={css.body}>
          <FormattedMessage
            id={`OnboardingModal.welcomeBody.${audience}.${welcomeVariant}.intro`}
          />
        </p>
        <p className={css.body}>
          <FormattedMessage
            id={`OnboardingModal.welcomeBody.${audience}.${welcomeVariant}.steps`}
          />
        </p>
      </>
    ),
    phone: codePhase.codeSent ? (
      <FormattedMessage
        id="OnboardingModal.phoneCodeBody"
        values={{ number: <b>{codePhase.displayNumber}</b> }}
      />
    ) : (
      <FormattedMessage id="OnboardingModal.phoneBody" />
    ),
    tos: (
      <FormattedMessage
        id="OnboardingModal.tosBody"
        values={{
          termsLink: (
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="OnboardingModal.termsLinkText" />
            </a>
          ),
          privacyLink: (
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="OnboardingModal.privacyLinkText" />
            </a>
          ),
        }}
      />
    ),
    finalizing: <FormattedMessage id="OnboardingModal.finalizingBody" />,
    success: <FormattedMessage id={`OnboardingModal.successBody.${audience}`} />,
  };
  const bodyBlock =
    screen === 'welcome' ? (
      bodyByScreen.welcome
    ) : (
      <p className={css.body}>{bodyByScreen[screen]}</p>
    );

  const content = (
    <div className={css.overlay} role="dialog" aria-modal="true" aria-labelledby="onboardingTitle">
      <div className={css.card}>
        <div className={css.crown}>
          <div className={css.logo}>
            <span className={css.logoThe}>The</span> Premier Horse
          </div>
          <div className={css.crownMain}>
            {eyebrow ? <div className={css.eyebrow}>{eyebrow}</div> : null}
            <h2 id="onboardingTitle" className={css.headline}>
              {titleByScreen[screen]}
            </h2>
            {screen === 'success' ? (
              <div className={css.check}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 12.5l5.5 5.5L20 6.5" />
                </svg>
              </div>
            ) : null}
          </div>
        </div>

        <div className={css.lower}>
          <div className={css.lowerContent}>
            {dots}
            {screen === 'finalizing' ? <div className={css.spin} /> : null}
            {bodyBlock}
          </div>

          <div className={css.lowerActions}>
            {screen === 'error' ? (
              <Button rootClassName={css.cta} type="button" onClick={onReloadState}>
                <FormattedMessage id="OnboardingModal.retry" />
              </Button>
            ) : null}

            {screen === 'welcome' ? (
              <Button
                rootClassName={css.cta}
                type="button"
                onClick={() => setWelcomeAcknowledged(true)}
              >
                <FormattedMessage id="OnboardingModal.getStarted" />
              </Button>
            ) : null}

            {screen === 'phone' ? (
              <TwilioVerify
                initialPhone={getKnownPhone(currentUser)}
                onVerifyStart={verifyStart}
                onVerifyCheck={verifyCheck}
                onVerified={onPhoneVerified}
                onPhaseChange={setCodePhase}
              />
            ) : null}

            {screen === 'tos' ? (
              <Button rootClassName={css.cta} type="button" onClick={onAcceptTerms}>
                <FormattedMessage id="OnboardingModal.acceptAndContinue" />
              </Button>
            ) : null}

            {screen === 'finalizing' && finalizingStalled ? (
              <Button rootClassName={css.cta} type="button" onClick={onReloadState}>
                <FormattedMessage id="OnboardingModal.refresh" />
              </Button>
            ) : null}

            {screen === 'success' ? (
              <>
                <Button rootClassName={css.cta} type="button" onClick={onSuccessCta}>
                  <FormattedMessage id={`OnboardingModal.successCta.${audience}`} />
                </Button>
                <Button rootClassName={css.quiet} type="button" onClick={onSuccessQuiet}>
                  <FormattedMessage id={`OnboardingModal.successQuiet.${audience}`} />
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, portalRoot);
};

export default OnboardingModal;
